import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import CloseIcon from '@mui/icons-material/Close'
import { AlertProps } from '@mui/lab'
import { AlertTitle, IconButton } from '@mui/material'
import { GorillaAlert } from '@procom-labs/atoms'
import {
  CommonMessages,
  LocalStorageAttributes,
  removeProtectedLocalStorageAttribute,
} from '@procom-labs/common'

interface ICustomProps extends AlertProps {
  handleAlertClose?: () => void
}
const CustomAlert: FC<ICustomProps> = React.memo(
  ({ handleAlertClose, children, ...props }) => {
    const { t } = useTranslation('main')

    return (
      <GorillaAlert
        sx={{ my: 3 }}
        {...props}
        action={
          handleAlertClose ? (
            <IconButton
              aria-label={t('common.aria.close')}
              color="inherit"
              size="small"
              onClick={handleAlertClose}
            >
              <CloseIcon />
            </IconButton>
          ) : null
        }
      >
        {children}
      </GorillaAlert>
    )
  }
)

export const ScreenAlert: FC = () => {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation('main')

  const [userHasLoggedOut, setUserHasLoggedOut] = useState<boolean>(false)
  const [userSessionExpired, setUserSessionExpired] = useState<boolean>(false)
  const [userNotAllowed, setUserNotAllowed] = useState<boolean>(false)
  const [userHasAccount, setUserHasAccount] = useState<boolean>(false)
  const [emailVerified, setEmailVerified] = useState<boolean>(false)
  const [newPassword, setNewPassword] = useState<boolean>(false)
  const [errorString, setErrorString] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (searchParams.get('accountExists')) setUserHasAccount(true)
    if (searchParams.get('emailVerified')) setEmailVerified(true)
    if (searchParams.get('new-password')) setNewPassword(true)

    if (searchParams.get('logout')) setUserHasLoggedOut(true)
    else if (searchParams.get('sessionExpired')) {
      removeProtectedLocalStorageAttribute(
        LocalStorageAttributes.IsResumeCopilotConsentGiven
      )
      setUserSessionExpired(true)
    } else if (searchParams.get('notAllowed')) setUserNotAllowed(true)

    const errorParam = searchParams.get('error') || ''
    if (
      [
        CommonMessages.SomethingWrong.toString(),
        CommonMessages.LoginFailed.toString(),
      ].includes(errorParam)
    ) {
      setErrorString(t('common.alert.somethingWrong'))
    }
    if (errorParam === CommonMessages.InvalidEmailDomain.toString()) {
      setErrorString(t('form.login.alerts.invalidEmailDomain'))
    }
  }, [t, searchParams])

  return (
    <>
      {emailVerified && (
        <CustomAlert severity="success">
          {t('form.login.alerts.verified')}
        </CustomAlert>
      )}

      {userHasLoggedOut && (
        <CustomAlert
          severity="success"
          handleAlertClose={() => setUserHasLoggedOut(false)}
        >
          {t('form.login.alerts.logout')}
        </CustomAlert>
      )}

      {userNotAllowed && (
        <CustomAlert
          severity="success"
          handleAlertClose={() => setUserNotAllowed(false)}
        >
          {t('form.login.alerts.notAllowed')}
        </CustomAlert>
      )}

      {userSessionExpired && (
        <CustomAlert severity="error">
          {t('form.login.alerts.sessionExpired')}
        </CustomAlert>
      )}

      {newPassword && (
        <CustomAlert severity="success">
          {t('form.login.alerts.resetPassword')}
        </CustomAlert>
      )}

      {!!errorString && (
        <CustomAlert severity="error" sx={{ mb: '24px' }}>
          {errorString}
        </CustomAlert>
      )}

      {userHasAccount && (
        <CustomAlert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>{t('form.login.alerts.accountExistsTitle')}</AlertTitle>
          {t('form.login.alerts.accountExists')}
        </CustomAlert>
      )}
    </>
  )
}
